import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { YesNoToggle } from '@app/components/core/toggle/YesNoToggle'
import { CreationStepLayout } from '../blocks/CreationStepLayout'
import { InitiativeAddressInput } from '../blocks/InitiativeAddressInput'
import {
  createInitiativeAddressState,
  parsePlotAddress,
  resetInitiativeAddressState,
} from '../blocks/initiativeAddressState'
import { LocalitySelector } from '@app/components/blocks/smartInputs/LocalitySelector'
import { RegionSelector } from '@app/components/blocks/smartInputs/RegionSelector'
import { SearchSelectOption } from '@app/components/core/input/searchSelect/SearchSelect'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInitiativeAutoSave } from '../../shared/useInitiativeAutoSave'
import { useTranslations } from '@i18n/useLanguage'
import { Style } from 'inlines'
import type { InitiativeWithId } from '../../shared/initiativeTypes'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../../shared/workflow'
import { PLOT_STATUS, type Initiative } from '@schema/initiative'
import { PHASE_TYPES, PhaseType } from '@schema/phase'

const CURRENT_STEP = initiativeCreationSteps.location

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}

const yesNoToggleProps = {
  optionStyle: { flexGrow: 1 },
  containerStyle: { width: '100%' },
} as const

const inferHasPlot = (initiative: InitiativeWithId): boolean =>
  initiative.potentialPlotStatus !== undefined &&
  initiative.potentialPlotStatus !== PLOT_STATUS.noPlot

export const LocationStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const commonTranslations = useTranslations().app.common

  const [hasPlot, setHasPlot] = useState(() => inferHasPlot(initiative))
  const [addressState, setAddressState] = useState(() =>
    createInitiativeAddressState(initiative),
  )
  const { address, resolvedAddress, geoCoords, resolvedAddressRefs } =
    addressState
  const [permit, setPermit] = useState(
    () =>
      initiative.permit ??
      (selectedPhaseType === PHASE_TYPES.construction ? true : false),
  )
  const [
    financingRequirementsConstruction,
    setFinancingRequirementsConstruction,
  ] = useState(initiative.financingRequirementsConstruction ?? false)
  const [financingRequirementsPlanning, setFinancingRequirementsPlanning] =
    useState(initiative.financingRequirementsPlanning ?? false)
  const [region, setRegion] = useState<SearchSelectOption>({
    key: initiative.location?.address?.region?.id ?? 0,
    value: initiative.location?.address?.region?.name ?? '',
  })
  const [locality, setLocality] = useState<SearchSelectOption>({
    key: initiative.location?.address?.locality?.id ?? 0,
    value: initiative.location?.address?.locality?.name ?? '',
  })

  const isEarlyPhase =
    selectedPhaseType === PHASE_TYPES.idea ||
    selectedPhaseType === PHASE_TYPES.location
  const isDesignPhase = selectedPhaseType === PHASE_TYPES.design
  const isConstructionPhase = selectedPhaseType === PHASE_TYPES.construction
  const showPlotFields = isDesignPhase || isConstructionPhase || hasPlot
  const showRegionFields = isEarlyPhase && !hasPlot
  const hasEnteredPlotAddress = address.trim().length > 0 && geoCoords !== null
  const showPermitToggle =
    showPlotFields &&
    hasEnteredPlotAddress &&
    (isDesignPhase || (isEarlyPhase && hasPlot))
  const showFinancingToggles = isDesignPhase || isConstructionPhase
  const effectivePermit = isConstructionPhase
    ? true
    : showPermitToggle
      ? permit
      : false

  useEffect(() => {
    if (isConstructionPhase) {
      setPermit(true)
    }
  }, [isConstructionPhase])

  const handleHasPlotChange = useCallback((value: boolean) => {
    setHasPlot(value)
    if (!value) {
      setAddressState(resetInitiativeAddressState())
      setRegion({ key: 0, value: '' })
      setLocality({ key: 0, value: '' })
      setPermit(false)
      setFinancingRequirementsConstruction(false)
      setFinancingRequirementsPlanning(false)
      return
    }
    setRegion({ key: 0, value: '' })
    setLocality({ key: 0, value: '' })
  }, [])

  const savePayload = useMemo((): Partial<Initiative> => {
    const trimmedAddress = address.trim()
    const parsed = parsePlotAddress(trimmedAddress)

    if (showPlotFields) {
      return {
        potentialPlotStatus: PLOT_STATUS.underOption,
        potentialPlotAddress: resolvedAddress || trimmedAddress,
        permit: effectivePermit,
        financingRequirementsConstruction: showFinancingToggles
          ? financingRequirementsConstruction
          : false,
        financingRequirementsPlanning: showFinancingToggles
          ? financingRequirementsPlanning
          : false,
        location: {
          address: {
            postalCode: parsed.postalCode,
            street: parsed.street || trimmedAddress || null,
            region: resolvedAddressRefs.region,
            locality: resolvedAddressRefs.locality,
            municipality: resolvedAddressRefs.municipality,
            country: resolvedAddressRefs.country,
          },
          geo: geoCoords ?? undefined,
        },
        onboardingStep: CURRENT_STEP,
      }
    }

    return {
      potentialPlotStatus: PLOT_STATUS.noPlot,
      potentialPlotAddress: '',
      permit: false,
      financingRequirementsConstruction: false,
      financingRequirementsPlanning: false,
      location: {
        address: {
          region: region.key > 0 ? { id: region.key } : null,
          locality: locality.key > 0 ? { id: locality.key } : null,
          postalCode: null,
          street: null,
          municipality: null,
          country: null,
        },
        geo: undefined,
      },
      onboardingStep: CURRENT_STEP,
    }
  }, [
    showPlotFields,
    showFinancingToggles,
    resolvedAddress,
    effectivePermit,
    financingRequirementsConstruction,
    financingRequirementsPlanning,
    address,
    geoCoords,
    resolvedAddressRefs,
    region.key,
    locality.key,
  ])

  useInitiativeAutoSave(initiative.id, savePayload)

  useEffect(() => {
    completeStep(CURRENT_STEP)
  }, [completeStep])

  const filterByRegionId = region.key > 0 ? region.key : undefined

  return (
    <CreationStepLayout
      selectedPhaseType={selectedPhaseType}
      currentStep={CURRENT_STEP}
    >
      <Column gap="2rem" alignItems="stretch">
        {isEarlyPhase && (
          <Column gap="0.5rem" alignItems="stretch">
            <Text.Medium style={labelStyle}>
              {translations.locationAlreadyHavePlot}
            </Text.Medium>
            <YesNoToggle
              yesSelected={hasPlot}
              setYesSelected={handleHasPlotChange}
              {...yesNoToggleProps}
            />
          </Column>
        )}

        {showPlotFields ? (
          <>
            <InitiativeAddressInput
              state={addressState}
              onStateChange={setAddressState}
              labelStyle={labelStyle}
            />
            {showPermitToggle && (
              <Column gap="0.5rem" alignItems="stretch">
                <Text.Medium style={labelStyle}>
                  {translations.locationPermitQuestion}
                </Text.Medium>
                <YesNoToggle
                  yesSelected={permit}
                  setYesSelected={setPermit}
                  {...yesNoToggleProps}
                />
              </Column>
            )}
            {showFinancingToggles && (
              <>
                <Column gap="0.5rem" alignItems="stretch">
                  <Text.Medium style={labelStyle}>
                    {translations.financingRequirementsConstruction}
                  </Text.Medium>
                  <YesNoToggle
                    yesSelected={financingRequirementsConstruction}
                    setYesSelected={setFinancingRequirementsConstruction}
                    {...yesNoToggleProps}
                  />
                </Column>
                <Column gap="0.5rem" alignItems="stretch">
                  <Text.Medium style={labelStyle}>
                    {translations.financingRequirementsPlanning}
                  </Text.Medium>
                  <YesNoToggle
                    yesSelected={financingRequirementsPlanning}
                    setYesSelected={setFinancingRequirementsPlanning}
                    {...yesNoToggleProps}
                  />
                </Column>
              </>
            )}
          </>
        ) : showRegionFields ? (
          <>
            <Text.Medium style={labelStyle}>
              {translations.locationBuildAndLiveHeading}
            </Text.Medium>
            <RegionSelector
              region={region}
              setRegion={setRegion}
              label={commonTranslations.region}
              labelStyle={labelStyle}
            />
            <LocalitySelector
              locality={locality}
              setLocality={setLocality}
              filterByRegionId={filterByRegionId}
              label={translations.locationLocality}
              labelStyle={labelStyle}
            />
          </>
        ) : null}
      </Column>
    </CreationStepLayout>
  )
}
